import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';

const formatNum = (n) => { if (!n) return '0'; if (n >= 1e6) return (n/1e6).toFixed(1)+'M'; if (n >= 1e3) return (n/1e3).toFixed(n>=1e4?0:1)+'K'; return String(n); };
const formatPct = (v) => v != null ? (parseFloat(v)*100).toFixed(1)+'%' : '-';
const tierCls = (t) => t===1?'bg-amber-500/20 text-amber-400':t===2?'bg-zinc-400/20 text-zinc-300':t===3?'bg-orange-800/20 text-orange-400':'bg-zinc-800 text-zinc-600';

const StatCard = ({ label, value, sub, color }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
    <div className="text-zinc-500 text-xs mb-1">{label}</div>
    <div className={`text-2xl font-bold ${color || 'text-white'}`}>{value}</div>
    {sub && <div className="text-zinc-600 text-xs mt-1">{sub}</div>}
  </div>
);

const AudBar = ({ label, value, color, maxVal }) => {
  const pct = value ? parseFloat(value) * 100 : 0;
  const width = maxVal ? (pct / maxVal * 100) : pct;
  return (
    <div className="flex items-center gap-3 py-1">
      <span className="text-zinc-400 text-xs w-20 text-right">{label}</span>
      <div className="flex-1 h-3 bg-zinc-800 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color || 'bg-nextide-turquoise'}`} style={{ width: `${Math.min(width, 100)}%` }} />
      </div>
      <span className="text-white text-xs w-12 font-medium">{formatPct(value)}</span>
    </div>
  );
};

const RankRow = ({ rank, item, children }) => (
  <div className="flex items-center gap-3 py-2 border-b border-zinc-800/50 last:border-0">
    <span className="text-zinc-600 text-xs w-5 text-right">{rank}</span>
    <Link to={`/talent/${item.id}`} className="text-white text-sm font-medium hover:text-nextide-turquoise flex-1 truncate">{item.name}</Link>
    <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${tierCls(item.talent_tier)}`}>T{item.talent_tier || '-'}</span>
    {children}
  </div>
);

const TalentMetrics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [benchmarks, setBenchmarks] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [rateData, setRateData] = useState(null);

  useEffect(() => {
    api.get('/talent-metrics').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get('/talent-benchmarks').then(r => setBenchmarks(r.data)).catch(() => {});
    api.get('/talent-revenue').then(r => setRevenue(r.data)).catch(() => {});
    api.get('/talent-rates').then(r => setRateData(r.data)).catch(() => {});
  }, []);

  if (loading) return <div className="p-8 text-zinc-500">Loading metrics...</div>;
  if (!data) return <div className="p-8 text-zinc-500">Failed to load metrics</div>;

  const { overview, categories, topCCV, topFollowing, mostBooked, campaignLeaders, audience } = data;

  return (
    <div className="p-8 min-w-0 overflow-y-auto">
      {/* Nav */}
      <div className="flex items-center gap-4 mb-1">
        <span className="text-white text-sm font-semibold border-b-2 border-nextide-turquoise pb-0.5">Metrics</span>
        <Link to="/talent" className="text-zinc-400 hover:text-white text-sm">Network</Link>
        <Link to="/talent-lists" className="text-zinc-400 hover:text-white text-sm">Lists</Link>
      </div>
      <h1 className="text-2xl font-bold text-white mb-6">Network Intelligence</h1>

      {/* Overview Cards */}
      {(() => {
        const avgCCV = Math.round(parseFloat(audience.avg_ccv) || 0);
        const availMonthlyImpr = (avgCCV * 12) * 20000;
        return (
          <div className="grid grid-cols-5 gap-4 mb-6">
            <StatCard label="Total Creators" value={overview.total} color="text-nextide-turquoise" />
            <StatCard label="Network Reach" value={formatNum(parseFloat(audience.network_reach) || 0)} sub="Combined followers" color="text-white" />
            <StatCard label="Monthly Avails" value={formatNum(availMonthlyImpr)} sub={`(${formatNum(avgCCV)} CCV × 12) × 20K hrs`} color="text-nextide-turquoise" />
            <StatCard label="Avg CCV" value={formatNum(avgCCV)} sub="Active creators" color="text-purple-400" />
            <StatCard label="Categories" value={categories.length} sub="Content types" color="text-white" />
          </div>
        );
      })()}

      {/* Tier + Status + Gender breakdown */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <div className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-3">By Tier</div>
          <div className="space-y-2">
            {[1,2,3].map(t => {
              const count = overview.byTier?.[t] || 0;
              const pct = overview.total > 0 ? (count / overview.total * 100).toFixed(0) : 0;
              return (
                <div key={t} className="flex items-center gap-3">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${tierCls(t)}`}>T{t}</span>
                  <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${t===1?'bg-amber-500':t===2?'bg-zinc-400':'bg-orange-500'}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-white text-xs w-8 text-right">{count}</span>
                  <span className="text-zinc-600 text-xs w-8">{pct}%</span>
                </div>
              );
            })}
            <div className="flex items-center gap-3 pt-1 border-t border-zinc-800">
              <span className="text-zinc-500 text-xs px-1.5">Untiered</span>
              <span className="text-zinc-600 text-xs ml-auto">{overview.total - Object.values(overview.byTier || {}).reduce((a,b)=>a+b,0)}</span>
            </div>
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <div className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-3">Performance Benchmarks</div>
          {benchmarks && (benchmarks.byTier?.length > 0 || benchmarks.byBrand?.length > 0) ? (
            <div>
              {/* By Tier */}
              {benchmarks.byTier?.length > 0 && (
                <div className="mb-4">
                  <div className="text-zinc-500 text-xs mb-2">By Tier</div>
                  <table className="w-full">
                    <thead><tr className="text-xs text-zinc-500 border-b border-zinc-800">
                      <th className="text-left pb-1">Tier</th>
                      <th className="text-right pb-1">Campaigns</th>
                      <th className="text-right pb-1">Total Impr.</th>
                      <th className="text-right pb-1">Avg ER</th>
                      <th className="text-right pb-1">Avg CTR</th>
                    </tr></thead>
                    <tbody>
                      {benchmarks.byTier.map(t => (
                        <tr key={t.talent_tier} className="text-xs border-b border-zinc-800/50">
                          <td className="py-1.5"><span className={`px-1.5 py-0.5 rounded font-semibold ${tierCls(t.talent_tier)}`}>T{t.talent_tier}</span>
                            <span className="text-zinc-600 ml-1">({t.creator_count} creators)</span></td>
                          <td className="py-1.5 text-white text-right">{t.campaign_count} <span className="text-zinc-600">({t.placement_count})</span></td>
                          <td className="py-1.5 text-nextide-turquoise text-right">{formatNum(Math.round(parseFloat(t.total_impressions)))}</td>
                          <td className="py-1.5 text-zinc-300 text-right">{t.avg_er ? parseFloat(t.avg_er).toFixed(2) + '%' : '-'}</td>
                          <td className="py-1.5 text-zinc-300 text-right">{t.avg_ctr ? parseFloat(t.avg_ctr).toFixed(2) + '%' : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {/* By Brand */}
              {benchmarks.byBrand?.length > 0 && (
                <div>
                  <div className="text-zinc-500 text-xs mb-2">By Brand</div>
                  <div className="max-h-[180px] overflow-y-auto">
                    <table className="w-full">
                      <thead><tr className="text-xs text-zinc-500 border-b border-zinc-800 sticky top-0 bg-zinc-900">
                        <th className="text-left pb-1">Brand</th>
                        <th className="text-right pb-1">Camp.</th>
                        <th className="text-right pb-1">Total Impr.</th>
                        <th className="text-right pb-1">Avg ER</th>
                        <th className="text-right pb-1">Avg CTR</th>
                      </tr></thead>
                      <tbody>
                        {benchmarks.byBrand.map(b => (
                          <tr key={b.brand} className="text-xs border-b border-zinc-800/50">
                            <td className="py-1 text-zinc-300 truncate max-w-[100px]">{b.brand}</td>
                            <td className="py-1 text-white text-right">{b.campaign_count}</td>
                            <td className="py-1 text-nextide-turquoise text-right">{formatNum(Math.round(parseFloat(b.total_impressions)))}</td>
                            <td className="py-1 text-zinc-300 text-right">{b.avg_er ? parseFloat(b.avg_er).toFixed(2) + '%' : '-'}</td>
                            <td className="py-1 text-zinc-300 text-right">{b.avg_ctr ? parseFloat(b.avg_ctr).toFixed(2) + '%' : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="text-zinc-500 text-sm">Not enough campaign data yet</div>
              <div className="text-zinc-600 text-xs mt-1">Finalize reporting periods to populate benchmarks</div>
            </div>
          )}
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <div className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-3">Monthly Avails by Geo</div>
          {(() => {
            const avgCCV = Math.round(parseFloat(audience.avg_ccv) || 0);
            const totalImpr = (avgCCV * 12) * 20000;
            const geos = [
              { label: 'USA', value: audience.avg_usa, color: 'bg-blue-500' },
              { label: 'Canada', value: audience.avg_canada, color: 'bg-red-500' },
              { label: 'UK', value: audience.avg_uk, color: 'bg-indigo-400' },
              { label: 'Australia', value: audience.avg_australia, color: 'bg-yellow-500' },
            ];
            const maxImpr = totalImpr * Math.max(...geos.map(g => parseFloat(g.value) || 0), 0.01);
            return (
              <div className="space-y-3">
                {geos.map(g => {
                  const pct = parseFloat(g.value) || 0;
                  const impr = Math.round(totalImpr * pct);
                  return (
                    <div key={g.label}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-zinc-300 text-xs">{g.label} ({(pct*100).toFixed(1)}%)</span>
                        <span className="text-white text-xs font-semibold">{formatNum(impr)}</span>
                      </div>
                      <div className="h-2.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${g.color}`} style={{ width: `${maxImpr > 0 ? (impr / maxImpr * 100) : 0}%` }} />
                      </div>
                    </div>
                  );
                })}
                <div className="flex items-center justify-between pt-2 border-t border-zinc-800 mt-2">
                  <span className="text-zinc-500 text-xs">Total Available</span>
                  <span className="text-nextide-turquoise text-xs font-bold">{formatNum(totalImpr)}</span>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Rankings Row */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <div className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-3">Top by CCV</div>
          {topCCV.map((t, i) => (
            <RankRow key={t.id} rank={i+1} item={t}>
              <span className="text-purple-400 text-xs font-medium">{formatNum(t.twitch_avg_ccv)}</span>
            </RankRow>
          ))}
          {topCCV.length === 0 && <div className="text-zinc-600 text-sm">No CCV data</div>}
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <div className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-3">Top by Following</div>
          {topFollowing.map((t, i) => (
            <RankRow key={t.id} rank={i+1} item={t}>
              <span className="text-nextide-turquoise text-xs font-medium">{formatNum(t.total_following)}</span>
            </RankRow>
          ))}
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-zinc-400 text-xs font-semibold uppercase tracking-wide">Top Performers</div>
            <Link to="/talent-performance" className="text-nextide-turquoise text-xs hover:underline">View All</Link>
          </div>
          {benchmarks?.topPerformers?.length > 0 ? (<>
            <div className="flex items-center gap-3 pb-1 mb-1 border-b border-zinc-700 text-zinc-500 text-xs">
              <span className="w-5"></span>
              <span className="flex-1">Creator</span>
              <span className="w-8">Tier</span>
              <span className="w-14 text-right">vs Tier ER</span>
            </div>
            {benchmarks.topPerformers.map((t, i) => {
            const erMult = parseFloat(t.er_multiplier) || 0;
            return (
              <div key={t.id} className="flex items-center gap-3 py-2 border-b border-zinc-800/50 last:border-0">
                <span className="text-zinc-600 text-xs w-5 text-right">{i+1}</span>
                <Link to={`/talent/${t.id}`} className="text-white text-sm font-medium hover:text-nextide-turquoise flex-1 truncate">{t.name}</Link>
                <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${tierCls(t.talent_tier)}`}>T{t.talent_tier || '-'}</span>
                <span className={`text-xs font-semibold w-14 text-right ${erMult >= 1 ? 'text-green-400' : 'text-red-400'}`}>{erMult.toFixed(1)}x</span>
              </div>
            );
          })}</>) : (
            <div className="text-zinc-600 text-sm">Not enough campaign data</div>
          )}
        </div>
      </div>

      {/* Revenue Attribution */}
      {revenue?.creators?.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="col-span-2 bg-zinc-900 border border-zinc-800 rounded-xl p-5">
            <div className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-3">Top Revenue Generators</div>
            <table className="w-full">
              <thead><tr className="text-xs text-zinc-500 border-b border-zinc-800">
                <th className="text-left pb-2 w-6"></th>
                <th className="text-left pb-2">Creator</th>
                <th className="text-left pb-2 w-10">Tier</th>
                <th className="text-right pb-2">Deals</th>
                <th className="text-right pb-2">Won Revenue</th>
                <th className="text-right pb-2">Pipeline</th>
                <th className="text-right pb-2">Total Attributed</th>
              </tr></thead>
              <tbody>
                {revenue.creators.slice(0, 10).map((c, i) => (
                  <tr key={c.id} className="text-xs border-b border-zinc-800/50">
                    <td className="py-2 text-zinc-600 text-right pr-2">{i+1}</td>
                    <td className="py-2"><Link to={`/talent/${c.id}`} className="text-white text-sm font-medium hover:text-nextide-turquoise">{c.name}</Link></td>
                    <td className="py-2"><span className={`px-1.5 py-0.5 rounded font-semibold ${tierCls(c.talent_tier)}`}>T{c.talent_tier || '-'}</span></td>
                    <td className="py-2 text-white text-right">{c.deal_count} <span className="text-zinc-600">({c.won_deals} won)</span></td>
                    <td className="py-2 text-green-400 text-right font-medium">${formatNum(Math.round(parseFloat(c.attributed_won)))}</td>
                    <td className="py-2 text-yellow-400 text-right">${formatNum(Math.round(parseFloat(c.attributed_pipeline)))}</td>
                    <td className="py-2 text-nextide-turquoise text-right font-bold">${formatNum(Math.round(parseFloat(c.attributed_total)))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
            <div className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-3">Revenue by Tier</div>
            {revenue.byTier?.length > 0 ? (
              <div className="space-y-4">
                {revenue.byTier.map(t => {
                  const won = Math.round(parseFloat(t.won_revenue) || 0);
                  const pipe = Math.round(parseFloat(t.pipeline_revenue) || 0);
                  const total = won + pipe;
                  const maxTotal = Math.max(...revenue.byTier.map(x => Math.round(parseFloat(x.won_revenue) || 0) + Math.round(parseFloat(x.pipeline_revenue) || 0)), 1);
                  return (
                    <div key={t.talent_tier}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${tierCls(t.talent_tier)}`}>Tier {t.talent_tier}</span>
                        <span className="text-white text-xs font-bold">${formatNum(total)}</span>
                      </div>
                      <div className="h-3 bg-zinc-800 rounded-full overflow-hidden flex">
                        <div className="h-full bg-green-500 rounded-l-full" style={{ width: `${total > 0 ? won/maxTotal*100 : 0}%` }} />
                        <div className="h-full bg-yellow-500" style={{ width: `${total > 0 ? pipe/maxTotal*100 : 0}%` }} />
                      </div>
                      <div className="flex justify-between mt-1 text-xs">
                        <span className="text-green-400">${formatNum(won)} won</span>
                        <span className="text-yellow-400">${formatNum(pipe)} pipeline</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <div className="text-zinc-600 text-sm">No deal data yet</div>}
          </div>
        </div>
      )}

      {/* Rate Intelligence */}
      {rateData?.rates?.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-6">
          <div className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-3">Rate Intelligence</div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="text-xs text-zinc-500 border-b border-zinc-800">
                <th className="text-left pb-2">Tier</th>
                <th className="text-left pb-2">Asset Type</th>
                <th className="text-left pb-2">Rate Type</th>
                <th className="text-right pb-2">Avg Rate</th>
                <th className="text-right pb-2">Min</th>
                <th className="text-right pb-2">Max</th>
                <th className="text-right pb-2">Avg Cost</th>
                <th className="text-right pb-2">Samples</th>
              </tr></thead>
              <tbody>
                {rateData.rates.map((r, i) => (
                  <tr key={i} className="text-xs border-b border-zinc-800/50">
                    <td className="py-1.5"><span className={`px-1.5 py-0.5 rounded font-semibold ${tierCls(r.talent_tier)}`}>T{r.talent_tier}</span></td>
                    <td className="py-1.5 text-zinc-300">{r.asset_type}</td>
                    <td className="py-1.5"><span className={`px-1.5 py-0.5 rounded text-xs ${r.rate_type === 'CPM' ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{r.rate_type}</span></td>
                    <td className="py-1.5 text-white text-right font-medium">${parseFloat(r.avg_rate).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                    <td className="py-1.5 text-zinc-400 text-right">${parseFloat(r.min_rate).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                    <td className="py-1.5 text-zinc-400 text-right">${parseFloat(r.max_rate).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                    <td className="py-1.5 text-nextide-turquoise text-right">{r.avg_cost ? '$' + formatNum(Math.round(parseFloat(r.avg_cost))) : '-'}</td>
                    <td className="py-1.5 text-zinc-500 text-right">{r.sample_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-zinc-600 text-xs mt-3">Sponsored Stream rule of thumb: $1.50 × CCV × hours</div>
        </div>
      )}

      {/* Audience Coverage */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <div className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-3">Avg Audience Demographics</div>
          <div className="text-zinc-500 text-xs mb-2">Gender</div>
          <AudBar label="Male" value={audience.avg_male} color="bg-blue-400" maxVal={100} />
          <AudBar label="Female" value={audience.avg_female} color="bg-pink-400" maxVal={100} />
          <div className="text-zinc-500 text-xs mt-3 mb-2">Age</div>
          <AudBar label="Under 18" value={audience.avg_u18} color="bg-orange-400" maxVal={100} />
          <AudBar label="18-34" value={audience.avg_18_34} color="bg-nextide-turquoise" maxVal={100} />
          <AudBar label="35+" value={audience.avg_35_plus} color="bg-violet-400" maxVal={100} />
          <div className="text-zinc-500 text-xs mt-3 mb-2">Race</div>
          <AudBar label="Black/AA" value={audience.avg_black} maxVal={100} />
          <AudBar label="White" value={audience.avg_white} maxVal={100} />
          <AudBar label="Asian" value={audience.avg_asian} maxVal={100} />
          <AudBar label="Hispanic" value={audience.avg_hispanic} maxVal={100} />
          <div className="text-zinc-500 text-xs mt-3 mb-2">Geography</div>
          <AudBar label="USA" value={audience.avg_usa} color="bg-blue-500" maxVal={100} />
          <AudBar label="Canada" value={audience.avg_canada} color="bg-red-500" maxVal={100} />
          <AudBar label="UK" value={audience.avg_uk} color="bg-indigo-400" maxVal={100} />
          <AudBar label="Australia" value={audience.avg_australia} color="bg-yellow-500" maxVal={100} />
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
          <div className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-3">Top Categories</div>
          <div className="space-y-1 max-h-[500px] overflow-y-auto">
            {categories.map((c, i) => (
              <div key={c.name} className="flex items-center gap-2 py-1">
                <span className="text-zinc-500 text-xs w-5 text-right">{i+1}</span>
                <span className="text-zinc-300 text-xs flex-1 truncate">{c.name}</span>
                <span className="text-white text-xs font-medium">{c.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TalentMetrics;
